import { useState, useEffect, ReactElement, ReactNode } from "react";
import classnamesBind from "classnames/bind";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { ProductImage } from "../../types/productTypes/product";
import styles from "./AiImageDisclosure.scss";

const classnames = classnamesBind.bind(styles);

const SHORT_LABEL_KEY = "pdp_image_disclaimer_short";

const FULL_LABEL_KEY_BY_ASSET_TYPE: Record<string, string> = {
  "Ai Generated": "pdp_image_disclaimer_long",
  "Ai Modified": "pdp_image_disclaimer_modified_long",
};

// "Ai Modified" disclosure is a future requirement; keep the mapping above but don't surface it yet.
const HIDDEN_AI_ASSET_TYPES = new Set(["Ai Modified"]);

interface AiImageDisclosureProps {
  image: Pick<ProductImage, "url" | "aiAssetType">;
  ariaHidden?: boolean;
  children: ReactNode;
}

const AiImageDisclosure = ({
  image,
  ariaHidden = false,
  children,
}: AiImageDisclosureProps): ReactElement => {
  const { features, getTranslation } = useRuntimeConstants() as {
    features?: { aiImageDisclosureEnabled?: boolean };
    getTranslation: (key: string) => string;
  };
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (ariaHidden) {
      setIsExpanded(false);
    }
  }, [ariaHidden]);

  useEffect(() => {
    setIsExpanded(false);
  }, [image.url]);

  const fullLabelKey =
    image.aiAssetType && !HIDDEN_AI_ASSET_TYPES.has(image.aiAssetType)
      ? FULL_LABEL_KEY_BY_ASSET_TYPE[image.aiAssetType]
      : undefined;

  if (!features?.aiImageDisclosureEnabled || !fullLabelKey) {
    return <>{children}</>;
  }

  const shortLabel = getTranslation(SHORT_LABEL_KEY);
  const fullLabel = getTranslation(fullLabelKey);

  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);

  return (
    <div
      className={styles.container}
      data-testid="ai-image-disclosure-container"
    >
      {children}
      <div
        className={classnames(styles.aiImageDisclosure, {
          [styles.expanded]: isExpanded,
        })}
        data-testid="ai-image-disclosure"
        role="button"
        aria-hidden={ariaHidden}
        tabIndex={ariaHidden ? -1 : 0}
        aria-expanded={isExpanded}
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleExpanded();
          }
        }}
        onBlur={() => setIsExpanded(false)}
      >
        <span aria-hidden="true">
          <span className={styles.label}>{shortLabel}</span>
          <span className={styles.expandedMessage}>{fullLabel}</span>
        </span>
        <span className={styles.visuallyHidden}>{fullLabel}</span>
      </div>
    </div>
  );
};

export default AiImageDisclosure;
